// The Google Apps Script the Meetings tab hands out. It runs in the user's own Google
// account every 5 minutes and sends each new Meet recording in Drive to their private feed
// link (backend: notetaker.import_drive_recording). The recording is shared by link only
// until CherryClipz says it has downloaded it (24 hours at the very most), then its sharing
// goes back to what it was.

export const buildMeetScript = (feedUrl: string): string => `// CherryClipz: sends every new Google Meet recording in your Drive to CherryClipz.
// Set up once: choose "setup" in the menu above and press Run, then allow access.
// This link is yours alone. Don't share this script with anyone.
const CHERRYCLIPZ_FEED = "${feedUrl}";
// Meet names every recording "<event title or meeting code> (2026-10-30 11:35 GMT+2)". It's found
// by that name anywhere in your Drive: not every account files them in a "Meet Recordings" folder.
const MEET_NAME = /\\(\\d{4}-\\d{2}-\\d{2} \\d{1,2}[:.]\\d{2} GMT[+-]\\d{1,2}(:\\d{2})?\\)(\\.mp4)?$/;
const NEW_FOR_DAYS = 3;        // older recordings are left alone
const CLOSE_AFTER_HOURS = 24;  // the share link is off by then even if CherryClipz never answered
const MAX_TRIES = 12;          // an hour of retries while Drive is still processing a file

function setup() {
  ScriptApp.getProjectTriggers().forEach(function (t) { ScriptApp.deleteTrigger(t); });
  ScriptApp.newTrigger("sendNewRecordings").timeBased().everyMinutes(5).create();
  sendNewRecordings();
}

function sendNewRecordings() {
  var props = PropertiesService.getUserProperties();
  var seen = JSON.parse(props.getProperty("cherryclipz") || "{}");  // file id -> {opened, sent, tries, closed, access, permission}
  var save = function () { props.setProperty("cherryclipz", JSON.stringify(seen)); };
  var now = Date.now();
  var since = new Date(now - NEW_FOR_DAYS * 864e5).toISOString().slice(0, 19);
  var found = 0;
  try {
    var files = DriveApp.searchFiles(
      'mimeType = "video/mp4" and modifiedDate > "' + since + '" and trashed = false and "me" in owners');
    while (files.hasNext()) {
      var file = files.next();
      var id = file.getId();
      var s = seen[id] || { sent: 0, tries: 0 };
      if (s.sent || s.tries >= MAX_TRIES || !MEET_NAME.test(file.getName())) continue;
      if (now - file.getDateCreated().getTime() > NEW_FOR_DAYS * 864e5) continue;
      if (!s.access) {
        // How the file was shared before we touched it, saved BEFORE the link opens, so a crash
        // mid-run can't leave the link open. Our own temporary link never counts as "before".
        var access = String(file.getSharingAccess()), permission = String(file.getSharingPermission());
        var ours = access === "ANYONE_WITH_LINK" && permission === "VIEW";
        s.access = ours ? "PRIVATE" : access;
        s.permission = ours ? "NONE" : permission;
        seen[id] = s;
        save();
      }
      s.opened = s.opened || now;  // saved before the link opens: the 24-hour close counts from here
      s.closed = false;
      seen[id] = s;
      save();
      try {
        file.setSharing(DriveApp.Access.ANYONE_WITH_LINK, DriveApp.Permission.VIEW);
        var res = UrlFetchApp.fetch(CHERRYCLIPZ_FEED, {
          method: "post", contentType: "application/json", muteHttpExceptions: true,
          payload: JSON.stringify({ file_id: id, name: file.getName(), attendees: guestsOf(file) }),
        });
        if (res.getResponseCode() !== 200) throw new Error(res.getContentText().slice(0, 300));
        s.sent = now;
        found += 1;
        if (JSON.parse(res.getContentText()).message === "already imported") close(id, s);
      } catch (e) {
        s.tries += 1;
        try { restore(file, s); s.closed = true; } catch (e2) {}
        console.warn(file.getName() + ": " + e);
      }
      seen[id] = s;
      save();
    }
    console.log(found ? "Sent " + found + " new Meet recording(s) to CherryClipz." : "No new Meet recordings.");
    Object.keys(seen).forEach(function (id) {
      var s = seen[id];
      // Any link this script opened is closed once CherryClipz has the file, or 24 hours after it
      // opened, sent or not (a run Google cut off mid-send still gets closed).
      try {
        if (s.opened && !s.closed &&
            (now - s.opened > CLOSE_AFTER_HOURS * 36e5 || (s.sent && downloaded(id)))) close(id, s);
      } catch (e) {
        console.warn("Couldn't check " + id + ": " + e);
      }
      if (s.closed && now - (s.opened || now) > 30 * 864e5) delete seen[id];
    });
  } finally {
    save();
  }
}

// Who was on the call, from the calendar event Meet named the recording after
// ("<event title> (2026-10-30 11:35 GMT+2)"). Shown in your email, never emailed.
function guestsOf(file) {
  try {
    var title = file.getName().replace(/\\s*\\(\\d{4}-\\d{2}-\\d{2}[^)]*\\)\\s*$/, "").trim();
    var made = file.getDateCreated();
    var events = CalendarApp.getDefaultCalendar().getEvents(new Date(made.getTime() - 12 * 36e5), made);
    var match = events.filter(function (e) { return e.getTitle() === title; }).pop();
    if (!match) return [];
    return match.getGuestList(true).map(function (g) { return { name: g.getName(), email: g.getEmail() }; });
  } catch (e) {
    return [];
  }
}

// CherryClipz has its own copy now, so the recording's sharing goes back to how it was.
function downloaded(id) {
  var res = UrlFetchApp.fetch(CHERRYCLIPZ_FEED, {
    method: "post", contentType: "application/json", muteHttpExceptions: true,
    payload: JSON.stringify({ file_id: id, check: true }),
  });
  return res.getResponseCode() === 200 && JSON.parse(res.getContentText()).downloaded === true;
}

function close(id, s) {
  try { restore(DriveApp.getFileById(id), s); } catch (e) {}
  s.closed = true;
}

// Put the file's link sharing back to what it was before (private, unless you'd shared it).
function restore(file, s) {
  file.setSharing(DriveApp.Access[s.access || "PRIVATE"], DriveApp.Permission[s.permission || "NONE"]);
}
`;
